import cx from 'classnames';
import type { FC, ReactNode, SyntheticEvent } from 'react';
import { useLocalStorage } from 'react-use';

import type { IconName } from '@zen/Styleguide';
import useTracking from '@zen/utils/hooks/useTracking';
import { createTrackingEvent, createTrackingLabelAttribute, createTrackingParentAttribute } from '@zen/utils/tracking';

import Icon from '../Icon';
import { closeIconColorMapping, defaultIcon, typeIconColorMapping, typeWrapperMapping } from './helpers';
import type { BannerMessageType, BannerWidth } from './types';

interface Props {
  bannerkey?: string;
  className?: string;
  icon?: IconName;
  iconPosition?: 'top' | 'bottom' | 'center';
  isClosable?: boolean;
  message: ReactNode;
  onClose?: () => void;
  type?: BannerMessageType;
  width?: BannerWidth;
}

export const displayName = 'Design System/Banner';

const Banner: FC<Props> = ({
  bannerkey = '',
  className,
  icon,
  iconPosition = 'center',
  isClosable = false,
  message,
  type = 'default',
  width = 'full',
  ...rest
}) => {
  const [isBannerDisplayed, saveIsBannerDisplayed] = useLocalStorage(`banner-${bannerkey}`, true);
  const { trackEvent } = useTracking();

  const isNarrow = width === 'narrow';
  const narrowWrapperClassName: string = cx(
    {
      'p-4': !className
    },
    typeWrapperMapping[type],
    className,
    'flex flex-col gap-3 w-80 max-w-[20rem] rounded-sm'
  );

  const wideWrapperClassName: string = cx(
    'flex justify-between  items-center',
    {
      'px-6 py-4 rounded-sm': !className
    },
    typeWrapperMapping[type],
    className
  );

  const iconWrapperClassName: string = cx(
    {
      'items-center': iconPosition === 'center',
      'items-start': iconPosition === 'top',
      'items-end': iconPosition === 'bottom'
    },
    'flex flex-1 min-w-0 gap-4'
  );

  if (isClosable && !isBannerDisplayed) {
    return null;
  }

  const handleClick = (event: SyntheticEvent): void => {
    const trackingEvent = createTrackingEvent(displayName, 'close', event);

    trackEvent(trackingEvent);

    saveIsBannerDisplayed(!isBannerDisplayed);
  };

  const renderIcon = (): ReactNode => {
    return <Icon className={cx('shrink-0 text-base', typeIconColorMapping[type])} icon={icon || defaultIcon[type]} />;
  };

  const renderCloseIcon = (): ReactNode => {
    return (
      <Icon className={cx('cursor-pointer shrink-0', closeIconColorMapping[type])} icon="zicon-close" onClick={handleClick} />
    );
  };

  const renderMessage = (): ReactNode => {
    return (
      <div className="min-w-0" {...createTrackingLabelAttribute(type)}>
        {message}
      </div>
    );
  };

  const narrowContent: ReactNode = (
    <>
      <div className="flex justify-between items-center shrink-0">
        {renderIcon()}
        {isClosable && renderCloseIcon()}
      </div>
      {renderMessage()}
    </>
  );

  const wideContent: ReactNode = (
    <>
      <div className={iconWrapperClassName} {...createTrackingLabelAttribute(type)}>
        {renderIcon()}
        {renderMessage()}
      </div>
      {isClosable && renderCloseIcon()}
    </>
  );

  const classNames: string = isNarrow ? narrowWrapperClassName : wideWrapperClassName;

  return (
    <div className={classNames} data-testid="banner-component" {...createTrackingParentAttribute('banner')} {...rest}>
      {isNarrow ? narrowContent : wideContent}
    </div>
  );
};

export type { Props as BannerProps };

export default Banner;
